import React, { useState } from "react";
import { useConsent } from "../contexts/ConsentContext";
import { useI18n } from "../contexts/I18nContext";
import { useLocation, Link } from "react-router-dom";
import { Cookie, Settings as Gear, ChevronDown } from "lucide-react";

export default function CookieBanner() {
  const { consent, set } = useConsent();
  const { lang } = useI18n();
  const location = useLocation();
  const [showCustomize, setShowCustomize] = useState(false);
  const [opts, setOpts] = useState({ analytics: false, marketing: false });

  // Don't show on legal/login/admin/owner — would cover the consent text.
  const path = location.pathname || "";
  if (path.startsWith("/admin") || path.startsWith("/owner") || path.startsWith("/login")) return null;
  if (consent) return null;

  const t = (ro, en) => (lang === "en" ? en : ro);

  const acceptAll = () => set({ analytics: true, marketing: true });
  const onlyNecessary = () => set({ analytics: false, marketing: false });
  const saveCustom = () => set({ analytics: opts.analytics, marketing: opts.marketing });

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-[55] p-4 sm:p-6"
      data-testid="cookie-banner"
      role="dialog"
      aria-label="cookie-consent"
    >
      <div
        className="max-w-5xl mx-auto card-surface p-5 sm:p-6 shadow-2xl"
        style={{ borderTop: "2px solid var(--brand-primary)" }}
      >
        <div className="flex items-start gap-4 mb-4">
          <Cookie className="w-6 h-6 shrink-0 mt-0.5" style={{ color: "var(--brand-primary)" }} />
          <div className="flex-1">
            <h3 className="font-serif text-lg sm:text-xl mb-2">
              {t("Folosim cookie-uri", "We use cookies")}
            </h3>
            <p className="text-sm leading-relaxed" style={{ color: "var(--brand-text-secondary)" }}>
              {t(
                "Acest site foloseste cookie-uri pentru a-ti oferi cea mai buna experienta. Cookie-urile necesare sunt esentiale pentru functionarea site-ului. Cele de analytics si marketing ne ajuta sa intelegem cum este folosit site-ul si sa-ti aratam continut relevant.",
                "This site uses cookies to give you the best experience. Necessary cookies are essential for the site. Analytics and marketing cookies help us understand site usage and show relevant content."
              )}{" "}
              <Link to="/confidentialitate" className="underline" style={{ color: "var(--brand-primary)" }}>
                {t("Politica de confidentialitate", "Privacy policy")}
              </Link>
            </p>
          </div>
        </div>

        {showCustomize && (
          <div className="border-t pt-4 mt-2 space-y-3 mb-4" style={{ borderColor: "var(--brand-border)" }} data-testid="customize-panel">
            <ConsentRow
              label={t("Necesare (obligatorii)", "Necessary (required)")}
              desc={t("Autentificare, preferinte limba, cos rezervare. Fara acestea site-ul nu functioneaza.", "Auth, language preferences, booking session. Site cannot function without these.")}
              checked
              disabled
            />
            <ConsentRow
              label={t("Analytics", "Analytics")}
              desc={t("Ne ajuta sa intelegem cum folosesti site-ul (Google Analytics). Fara date personale.", "Helps us understand site usage (Google Analytics). No personal data.")}
              checked={opts.analytics}
              onChange={(v) => setOpts({ ...opts, analytics: v })}
              testid="consent-analytics"
            />
            <ConsentRow
              label={t("Marketing", "Marketing")}
              desc={t("Permite afisarea de oferte personalizate si masurarea campaniilor (Facebook Pixel).", "Enables personalized offers and campaign tracking (Facebook Pixel).")}
              checked={opts.marketing}
              onChange={(v) => setOpts({ ...opts, marketing: v })}
              testid="consent-marketing"
            />
          </div>
        )}

        <div className="flex flex-wrap gap-2 justify-end">
          {!showCustomize ? (
            <>
              <button
                onClick={() => setShowCustomize(true)}
                className="btn-ghost flex items-center gap-2 text-xs"
                data-testid="consent-customize-btn"
              >
                <Gear className="w-3.5 h-3.5" />
                {t("Personalizeaza", "Customize")}
              </button>
              <button onClick={onlyNecessary} className="btn-ghost text-xs" data-testid="consent-only-necessary">
                {t("Doar necesare", "Only necessary")}
              </button>
              <button onClick={acceptAll} className="btn-gold-filled text-xs" data-testid="consent-accept-all">
                {t("Accept toate", "Accept all")}
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setShowCustomize(false)} className="btn-ghost text-xs flex items-center gap-2">
                <ChevronDown className="w-3.5 h-3.5" />
                {t("Inapoi", "Back")}
              </button>
              <button onClick={saveCustom} className="btn-gold-filled text-xs" data-testid="consent-save-custom">
                {t("Salveaza preferinte", "Save preferences")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ConsentRow({ label, desc, checked, onChange, disabled, testid }) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? "opacity-70" : "cursor-pointer"}`}>
      <input
        type="checkbox"
        checked={!!checked}
        disabled={!!disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
        className="mt-1 shrink-0"
        data-testid={testid}
      />
      <div className="flex-1">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>{desc}</div>
      </div>
    </label>
  );
}
